import {
  AutomaticStatusWaiting,
  TimestampStatusCreating,
} from '@/features/approving_invoice_reports/types/status';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import { Tag, TagProps, Tooltip } from '@moneyforward/ap-frontend-components';
import { ApprovingReportStatus, ErrorWithLabelMessage } from 'ap-openapi';
import classnames from 'classnames/bind';
import { FC, memo } from 'react';
import { useAutomaticStatusTags } from '../../hooks/useAutomaticStatusTags';
import { useInfoTagCell } from '../../hooks/useInfoTagCell';
import styles from './InfoTagCell.module.scss';

const cx = classnames.bind(styles);

export type InfoTagCellProps = {
  errorMessages?: ErrorWithLabelMessage[];
  messageType: string[];
  children?: React.ReactNode;
  automaticStatusWaiting?: AutomaticStatusWaiting;
  timestampStatusCreating?: TimestampStatusCreating;
  receiptType?: string;
  status?: ApprovingReportStatus;
};

export const InfoTagCell: FC<InfoTagCellProps> = memo(
  ({
    errorMessages,
    messageType,
    children,
    automaticStatusWaiting,
    timestampStatusCreating,
    receiptType,
  }) => {
    const container = useGlobalContainerRef();
    const { filteredMessages } = useInfoTagCell({
      errorMessages,
      messageType,
    });
    const { statusTags } = useAutomaticStatusTags({
      automaticStatusWaiting,
      timestampStatusCreating,
    });

    const hasErrorMessages = filteredMessages.length > 0;
    const hasStatusTags = statusTags.length > 0;
    const hasReceiptType = !!receiptType;
    const isNotCreating =
      timestampStatusCreating !== TimestampStatusCreating.CREATING;

    if (!hasErrorMessages && !hasStatusTags && !hasReceiptType) {
      return <>{children}</>;
    }
    return (
      <div className={cx('infoTagCell')}>
        <div>{children}</div>
        <div className={cx('tags')}>
          {/* Render receipt type tag first */}
          {isNotCreating && !hasErrorMessages && hasReceiptType && (
            <div className={cx('tagContainer')}>
              <Tag className={cx('tag')} closable={false} outline={false}>
                {receiptType}
              </Tag>
            </div>
          )}

          {/* Render automatic status tags */}
          {statusTags.map((statusTag) => (
            <div key={statusTag.key} className={cx('tagContainer')}>
              <Tag
                className={cx('tag', statusTag.status)}
                closable={false}
                outline={false}
                status={statusTag.status}
              >
                {statusTag.label}
              </Tag>
            </div>
          ))}

          {/* Render error messages */}
          {isNotCreating &&
            filteredMessages.map((msg, index) => {
              const status: TagProps['status'] =
                msg.type === 'warn' ? 'warning' : 'error';

              return (
                <Tooltip
                  key={`${msg.message_type}-${index}`}
                  title={msg.message}
                  placement='top'
                  overlayClassName={
                    msg.message_type === 'duplicate_reports_message'
                      ? cx('duplicateReportsTooltipContainer')
                      : cx('tooltipContainer')
                  }
                  getPopupContainer={container}
                >
                  <div className={cx('tagContainer')}>
                    <Tag
                      className={cx('tag', status)}
                      closable={false}
                      outline={false}
                      status={status}
                      icon
                    >
                      {msg.label}
                    </Tag>
                  </div>
                </Tooltip>
              );
            })}
        </div>
      </div>
    );
  }
);

InfoTagCell.displayName = 'InfoTagCell';
